import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPlan, getRun, isTerminal, pollRun, pollPlan } from '../../../services/agent-eval';
import ScenarioDetails, { PlanCoverage } from '../../agent/agent-eval/scenario-details';
import { HiBeaker } from 'react-icons/hi2';
import { chatCard, chatPrimary, chatSecondary } from './chat-theme';
import CardHeader from './card-header';
import { StatusPill } from '../../agent/agent-eval/components';

const buttonClass = chatSecondary;

export function TestingOffer({ onSend, disabled }) {
    const [dismissed, setDismissed] = useState(false);
    if (dismissed) return null;
    return <section className={`${chatCard('knowledge')} space-y-3`} aria-label="Test your changes">
        <CardHeader icon={HiBeaker} tone="knowledge" title="Would you like to test these changes?" className="" />
        <p className="text-sm leading-relaxed text-gray-600">Copilot creates test conversations for everything your agent is connected to, then runs them and shows what passed.</p>
        <div className="flex flex-wrap gap-2">
            <button className={chatPrimary} disabled={disabled} onClick={() => onSend('Create automated test scenarios for my agent.')}>Create tests</button>
            <button className={buttonClass} disabled={disabled} onClick={() => setDismissed(true)}>Not now</button>
        </div>
    </section>;
}

export default function TestingCard({ testing, appId, onSend, disabled, actionable }) {
    const [plan, setPlan] = useState(null);
    const [run, setRun] = useState(null);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState([]);
    const [retry, setRetry] = useState(0);
    const { planId, runId } = testing;
    useEffect(() => {
        if (!appId) return undefined;
        const controller = new AbortController();
        let stopped = false;
        setError(''); setPlan(null); setRun(null);
        (async () => {
            try {
                if (planId) {
                    let data = await getPlan(appId, planId);
                    if (stopped) return;
                    if (!data && !runId) throw new Error('Could not load the scenarios. Try again.');
                    setPlan(data);
                    if (!runId && data?.generationStatus === 'generating') {
                        data = await pollPlan(appId, planId, value => { if (!stopped) setPlan(value); }, { signal: controller.signal });
                        if (stopped) return;
                        setPlan(data);
                    }
                    setSelected((data?.categories || []).flatMap(c => c.cases || []).filter(c => c.enabled !== false).map(c => c.id));
                }
                if (runId) {
                    const data = await getRun(appId, runId, true);
                    if (stopped) return;
                    if (!data) throw new Error('Could not load test progress. Try again.');
                    setRun(data);
                    if (!isTerminal(data.status)) {
                        const finished = await pollRun(appId, runId, value => { if (!stopped) setRun(value); }, { signal: controller.signal, includeCases: true });
                        if (!stopped) {
                            setRun(finished);
                            if (!finished || !isTerminal(finished.status)) setError('Still waiting for results. Resume tracking or open Agent Tests.');
                        }
                    }
                }
            } catch (e) { if (!stopped) setError(e.message || 'Could not load tests.'); }
        })();
        return () => { stopped = true; controller.abort(); };
    }, [appId, planId, runId, retry]);
    if (!appId) return null;
    const cases = (plan?.categories || []).flatMap(c => c.cases || []);
    const results = new Map((run?.cases || []).map(c => [c.caseId, c]));
    // Results remain visible if the original plan was deleted.
    const scenarios = run && isTerminal(run.status)
        ? (run.cases || []).map(c => ({ id: c.caseId, name: c.name }))
        : cases.filter(c => !run?.caseIds?.length || run.caseIds.includes(c.id));
    const path = `/app/${encodeURIComponent(appId)}/evaluation?${runId ? `runId=${encodeURIComponent(runId)}` : `planId=${encodeURIComponent(planId)}`}`;
    return <section className={`${chatCard('planning')} space-y-3 text-[0.875rem]`} aria-label="Agent tests">
        <CardHeader icon={HiBeaker} title={<h3>{runId ? 'Test results' : 'Test scenarios'}</h3>} className="">{run && <StatusPill status={run.status} />}</CardHeader>
        {!plan && !run && !error && <p role="status">Loading scenarios…</p>}
        {run && <p role="status" className="text-[0.8rem] text-gray-600">{run.summary?.completed || 0} of {run.summary?.total || 0} finished · {run.summary?.passed || 0} passed · {run.summary?.failed || 0} failed · {run.summary?.errored || 0} errors · {(run.summary?.cancelled || 0) + (run.summary?.skipped || 0)} not run</p>}
        {run?.error && <p role="alert">{run.error}</p>}
        {error && <div role="alert">{error} <button className={buttonClass} onClick={() => setRetry(r => r + 1)}>Retry</button></div>}
        {!runId && <PlanCoverage plan={plan} compact />}
        <div className="max-h-96 overflow-y-auto space-y-2">
            {scenarios.map(scenario => {
                const result = results.get(scenario.id);
                return <div key={scenario.id} className="rounded-xl border border-gray-200 bg-white p-3">
                    {!runId && <label className="flex gap-2 mb-2"><input type="checkbox" checked={selected.includes(scenario.id)} disabled={disabled || !actionable} onChange={e => setSelected(ids => e.target.checked ? [...ids, scenario.id] : ids.filter(id => id !== scenario.id))} />Include scenario</label>}
                    <details><summary className="cursor-pointer text-sm font-medium">{scenario.name} {result && <StatusPill status={result.status} />}</summary>
                        <div className="pt-3 space-y-2">
                            {scenario.turns && <ScenarioDetails scenario={scenario} />}
                            {(result?.reason || result?.error) && <p className="text-sm font-medium">{result.reason || result.error}</p>}
                            {(result?.turns || []).map((turn, i) => <div className="text-sm" key={i}><p>Customer: {turn.message}</p>{(turn.replies || []).map((reply, j) => <p key={j}>Agent: {reply}</p>)}</div>)}
                        </div>
                    </details>
                </div>;
            })}
        </div>
        {!runId && plan && actionable && !['generating', 'error'].includes(plan.generationStatus) && <>
            <p className="text-xs text-gray-600">Tests use the live agent and may call its connected tools.</p>
            <button className={chatPrimary} disabled={disabled || !selected.length} onClick={() => onSend(selected.length === cases.filter(c => c.enabled !== false).length ? `Run all tests in plan ${planId}.` : `Run the test plan ${planId}, only scenario IDs: ${selected.join(', ')}.`)}>Run {selected.length} scenarios</button>
            <p className="text-xs text-gray-600">To change a scenario, describe the change in chat.</p>
        </>}
        {run && isTerminal(run.status) && actionable && <button className={buttonClass} disabled={disabled} onClick={() => onSend(`Review test run ${runId}. Explain any failed checks, errors or scenarios that did not run, and suggest fixes.`)}>Explain results</button>}
        <Link className="inline-flex text-[0.8rem] font-medium text-gray-700 underline underline-offset-2 hover:text-gray-900" to={path}>Open Agent Tests</Link>
    </section>;
}
